import { useEffect, useId, useState } from 'react';

// ```mermaid blocks drawn as diagrams wherever Markdown is shown (sessions, files, tickets). Mermaid is a large
// library: it loads on the first diagram only. `strict` keeps the diagrams inert (no HTML labels, no click handlers),
// as the text comes from repositories and sessions. A diagram that does not parse stays as code, with the error.

type MermaidApi = typeof import('mermaid')['default'];
let loading: Promise<MermaidApi> | null = null;

function loadMermaid(): Promise<MermaidApi> {
  loading ??= import('mermaid').then((m) => m.default);
  return loading;
}

/** The app's tokens of the current theme, as Mermaid theme variables */
function themeVariables(): Record<string, string> {
  const css = getComputedStyle(document.documentElement);
  const token = (name: string) => css.getPropertyValue(name).trim();
  return {
    fontFamily: 'Geist, system-ui, sans-serif',
    fontSize: '13px',
    background: token('--color-surface-2'),
    primaryColor: token('--color-surface-3'),
    primaryTextColor: token('--color-text'),
    primaryBorderColor: token('--color-border-2'),
    secondaryColor: token('--color-surface'),
    tertiaryColor: token('--color-surface-2'),
    lineColor: token('--color-text-3'),
    textColor: token('--color-text'),
    noteBkgColor: token('--color-brand-dim'),
    noteTextColor: token('--color-text'),
    noteBorderColor: token('--color-brand'),
    clusterBkg: token('--color-surface'),
    clusterBorder: token('--color-border-2'),
    edgeLabelBackground: token('--color-surface-2'),
  };
}

/** Changes when the operator switches the theme, or the system does in auto mode */
function useThemeKey(): string {
  const read = () => `${document.documentElement.getAttribute('data-theme') ?? 'auto'}:${window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark'}`;
  const [key, setKey] = useState(read);
  useEffect(() => {
    const update = () => setKey(read());
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const media = window.matchMedia?.('(prefers-color-scheme: light)');
    media?.addEventListener('change', update);
    return () => {
      observer.disconnect();
      media?.removeEventListener('change', update);
    };
  }, []);
  return key;
}

export function MermaidDiagram({ code }: { code: string }) {
  const id = `mermaid-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;
  const theme = useThemeKey();
  const [state, setState] = useState<{ svg?: string; error?: string }>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const mermaid = await loadMermaid();
        mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'base', themeVariables: themeVariables() });
        const { svg } = await mermaid.render(id, code);
        if (!cancelled) setState({ svg });
      } catch (err) {
        // A failed render leaves its temporary element behind
        document.getElementById(`d${id}`)?.remove();
        if (!cancelled) setState({ error: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, id, theme]);

  if (state.svg) {
    return (
      <div
        className="mermaid-diagram my-2 overflow-x-auto rounded-md p-3"
        style={{ backgroundColor: 'var(--color-surface-2)' }}
        role="img"
        aria-label="Diagram"
        data-testid="mermaid-diagram"
        // Sanitised by Mermaid itself (DOMPurify) under securityLevel strict
        dangerouslySetInnerHTML={{ __html: state.svg }}
      />
    );
  }
  return (
    <div className="my-2" data-testid="mermaid-source">
      <pre className="overflow-x-auto rounded-md p-3 text-xs" style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text)' }}>
        <code>{code}</code>
      </pre>
      {state.error && (
        <p className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>
          This diagram could not be drawn: {state.error.split('\n')[0]}
        </p>
      )}
    </div>
  );
}
